import React, { useState, useEffect } from "react";
import "./Conseil.css";
import BarProgression from "../../views/Graph/BarProgression";
import minuteur from "../../ressources/images/minuteur.svg";
import { ConseilResponse } from "../../typings/types";

type ConseilArray = {
  high: string[];
  medium: string[];
  low: string[];
  veryLow: string[];
};

const Conseil = ({ conseil }: { conseil: ConseilResponse }) => {
  const { temps_travail, temps_ralenti } = conseil;

  // Inicializar os estados com strings vazias
  const [conseilFonctionnement, setConseilFonctionnement] = useState("");
  const [conseilRalenti, setConseilRalenti] = useState("");
  const [pourcentageFonctionnement, setPourcentageFonctionnement] = useState(0);
  const [pourcentageRalenti, setPourcentageRalenti] = useState(0);
  const [tempsDeRalentis, setTempsDeRalentis] = useState<string | number>(0);
  const [tempsDeTravail, setTempsDeTravail] = useState<string | number>(0);

  const conseilsFonctionnement: ConseilArray = {
    high: [
      "Vous assurez un fonctionnement fiable et efficace sur la période d'utilisation de la machine, poursuivez vos efforts !",
      "Votre engagement à maintenir les engins en excellent état démontre votre détermination à garantir leur durabilité et leur performance.",
      "Votre expertise et votre vigilance ont évité interruptions et retards, témoignant de votre engagement envers l'efficacité et la qualité du travail.",
      "En minimisant les périodes d'inactivité, vous maximisez l'efficacité du travail et réduisez la consommation de carburant et les émissions de CO2.",
      "Les conditions de fonctionnement de la machine sont optimales, continuez comme ça !",
      "Les performances de la machine sont bonnes, vous êtes sur la bonne voie !",
    ],
    medium: [
      "Veillez à ce que les axes des parties articulées soient suffisamment graissées",
      "Pensez à vérifier l'état du train de chenille",
      "Faîtes régulièrement le contrôle des pièces d'usure et la vérification du filtre à air",
      "Changer les filtres peut améliorer considérablement l'efficacité énergétique",
      "Limitez l'usage d'accessoires non indispensables, pouvant surcharger le moteur et augmenter la consommation de carburant.",
      "Programmez un nettoyage régulier de la machine pour éviter les dommages aux composants et les obstructions du système de refroidissement.",
    ],
    low: [
      "Maintenir une pression optimale des pneus peut améliorer considérablement l'efficacité énergétique",
      "Vérifiez si le mode de changement de vitesse est automatique ou manuel (le mode automatique est privilégié).",
      "Sélectionnez les outils de travail adaptés au type de travail",
      "Maintenez l'engin en bon état de fonctionnement",
      "Maintenez une vitesse constante lors du fonctionnement de la machine pour limiter les variations de consommation de carburant",
      "Optimisez les déplacements de la machine pour éviter les trajets inutiles. Réduisez les distances de déplacement et les mouvements au minimum.",
    ],
    veryLow: [
      "Effectuez régulièrement l'entretien des moteurs",
      "Sélectionnez le mode économique approprié si la machine en dispose, adapté à la tâche à accomplir (par exemple, mode P/E).",
      "Assurez un bon réglage des outils de travail",
      "Ne dépassez pas les limites de votre équipement, vous risquez d'endommager l'engin et de réduire ses performances",
      "Utilisez le rapport ou la vitesse appropriée pour le terrain et la tâche",
      "Priviliez le système de ralentissement pour stopper l'engin, réservant le freinage à l'immobilisation finale.",
    ],
  };

  const conseilsRalenti: ConseilArray = {
    high: [
      "L'utilisation de la machine au ralenti respecte les normes d'éco-conduite",
      "L'empreinte carbone de la machine est en dessous de la moyenne, vous adoptez une bonne éco-conduite",
      "Votre conduite économise du carburant, réduit les coûts de maintenance et prolonge la durée de vie du matériel. Continuez ainsi !",
    ],
    medium: [
      "Veillez à supprimer les déplacements et les reprises inutiles de matériaux",
      "Favorisez lorsqu'il est disponible l'utilisation du système d'arrêt automatique \"Stop and Start\"",
      "Optimisez les temps de ralenti et respectez les procédures d'entretien pour prolonger la durée de vie des machines et réduire les coûts opérationnels",
    ],
    low: [
      "Planifiez les tâches efficacement pour réduire les temps d'attente et les déplacements, économisant ainsi du carburant.",
      "Choisissez le régime moteur et le rapport de transmission adapté à la tâche à réaliser",
      "Limitez l'usage d'accessoires supplémentaires pour éviter de surcharger le moteur et réduire la consommation de carburant.",
    ],
    veryLow: [
      "Arrêtez les engins qui attendent pour charger ou décharger",
      "Limitez la mise en marche et le réchauffement du moteur trop longtemps",
      "Réduisez les temps de ralenti pour prolonger la durée de vie des moteurs et réduire la consommation de carburant.",
    ],
  };

  // Atualizar os conselhos quando as porcentagens mudarem
  useEffect(() => {
    let pourcentageFonctionnement = 0;
    let pourcentageRalenti = 0;
    let temps_de_ralentis: string | number = 0;
    let temps_de_travail: string | number = 0;
    let tempsFonctionnement;

    // Verificar se as variáveis necessárias estão disponíveis e válidas
    const variablesIndefinidas =
      temps_travail === null ||
      temps_travail === undefined ||
      temps_travail === 0;
    function formatarHorasMinutos(valorMinutos: number) {
      // Arredonda o número de minutos para evitar frações muito grandes
      const minutosArredondados = Math.round(valorMinutos);

      // Converte para horas e minutos
      const horas = Math.floor(minutosArredondados / 60);
      const minutos = minutosArredondados % 60;

      // Caso queira sempre ter dois dígitos para os minutos (ex.: “6H01”):
      // return `${horas}H${String(minutos).padStart(2, "0")}`;

      // Caso contrário, só retorna “6H1” ou “6H16”
      return `${horas}H${
        minutos
          ? minutos.toLocaleString("fr-FR", {
              minimumIntegerDigits: 2,
            })
          : ""
      }`;
    }

    if (!variablesIndefinidas) {
      // Calcular tempos em horas e minutos
      // temps_de_ralentis = Math.floor(temps_ralenti / 60) + "H" + (temps_ralenti % 60);
      // temps_de_travail = Math.floor(temps_travail / 60) + "H" + (temps_travail % 60);
      temps_de_ralentis = formatarHorasMinutos(temps_ralenti);
      temps_de_travail = formatarHorasMinutos(temps_travail);

      // Calcular temps de fonctionnement
      tempsFonctionnement = temps_travail - temps_ralenti;

      // Garantir que temps_travail não seja zero para evitar divisão por zero
      if (temps_travail > 0) {
        pourcentageFonctionnement = (tempsFonctionnement / temps_travail) * 100;
        pourcentageRalenti = 100 - (temps_ralenti / temps_travail) * 100;
      } else {
        pourcentageFonctionnement = 0;
        pourcentageRalenti = 0;
      }
    }
    setPourcentageFonctionnement(pourcentageFonctionnement);
    setPourcentageRalenti(pourcentageRalenti);
    setTempsDeRalentis(temps_de_ralentis);
    setTempsDeTravail(temps_de_travail);

    if (
      pourcentageFonctionnement !== undefined &&
      !isNaN(pourcentageFonctionnement)
    ) {
      setConseilFonctionnement(
        getConseil(pourcentageFonctionnement, conseilsFonctionnement)
      );
    }
    if (pourcentageRalenti !== undefined && !isNaN(pourcentageRalenti)) {
      setConseilRalenti(getConseil(pourcentageRalenti, conseilsRalenti));
    }
  }, [conseil]);

  // Função para atualizar os conselhos ao clicar no botão
  const handleShowMoreConseils = () => {
    setConseilFonctionnement(
      getConseil(pourcentageFonctionnement, conseilsFonctionnement)
    );
    setConseilRalenti(getConseil(pourcentageRalenti, conseilsRalenti));
  };

  // Se as porcentagens não estiverem definidas, exibir a mensagem
  if (
    pourcentageFonctionnement === undefined ||
    pourcentageRalenti === undefined ||
    temps_travail === 0
  ) {
    return (
      <div className="tab-content">
        <button className="btnMsg">
          Pas de données enregistrées pour cette période. Essayez une autre
          plage de dates !
        </button>
      </div>
    );
  }

  // Conteúdo normal se as porcentagens estiverem definidas
  return (
    <div className="tab-content">
      <div className="Conseil-main">
        <div className="Ralenti">
          <div className="hStyle">Ralenti</div>
          <div className="rotate180">
            <BarProgression pourcentage={pourcentageRalenti} />
          </div>

          <div className="space">
            <img src={minuteur} className="minuteur" alt="minuteur" />
            {tempsDeRalentis} sur {tempsDeTravail} de travail
          </div>
          <div className="Scon">
            <div className="conseilhead">Conseil de ralenti : </div>
            <span style={{ textAlign: "justify" }}>{conseilRalenti}</span>
          </div>

          <div className="space Scon">
            <div className="conseilhead">Conseil de fonctionnement : </div>
            <span style={{ textAlign: "justify" }}>
              {conseilFonctionnement}
            </span>
          </div>
        </div>
        <button onClick={handleShowMoreConseils} className="conseil">
          Affichez plus de conseils
        </button>
      </div>
    </div>
  );

  // Função para obter um conseil aleatório com base na porcentagem
  function getConseil(pourcentage: number, conseils: ConseilArray) {
    const { high, medium, low, veryLow } = conseils;

    if (pourcentage === undefined || isNaN(pourcentage)) {
      return ""; // Ou retorne uma mensag em padrão
    } else if (pourcentage >= 75) {
      return conseils.high[Math.floor(Math.random() * high.length)];
    } else if (pourcentage >= 50) {
      return conseils.medium[Math.floor(Math.random() * medium.length)];
    } else if (pourcentage >= 25) {
      return conseils.low[Math.floor(Math.random() * low.length)];
    } else {
      return conseils.veryLow[Math.floor(Math.random() * veryLow.length)];
    }
  }
};

export default Conseil;
